import { Box, Typography } from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "react-bootstrap";
import PrimaryButton from '../../PrimaryButton';
// Remove AOS import if not used
import 'aos/dist/aos.css';

// Utility function to extract YouTube video ID from URL or return ID directly
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  // Handle various YouTube URL formats
  const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
  const match = url.match(regex);
  return match ? match[1] : url; // Return match[1] if URL, or url if it's already an ID
};

const Gallery = ({ gallery = [] }) => {
  // Initialize state for images and videos
  const [allImages, setAllImages] = useState([]);
  const [currentImageSet, setCurrentImageSet] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [gallVideo, setGallVideo] = useState([]);
  
  const IMAGES_PER_SET = 4;
  const ROTATION_INTERVAL = 5000; // 5 seconds

  // Process gallery data on mount or when gallery prop changes
  useEffect(() => {
    const images = gallery.filter((item) => item.image !== null);
    const videos = gallery.filter((item) => item.video !== null).slice(0, 4);
    
    setAllImages(images);
    setGallVideo(videos);
    
    // Initialize with first set of images
    if (images.length > 0) {
      setCurrentImageSet(images.slice(0, Math.min(IMAGES_PER_SET, images.length)));
    }
  }, [gallery]);

  // Auto-rotate images every 5 seconds
  useEffect(() => {
    if (allImages.length <= IMAGES_PER_SET) return; // No need to rotate if all images fit in one set
    
    const intervalId = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        const nextIndex = (prevIndex + IMAGES_PER_SET) % allImages.length;
        
        // Get next set of images (handle wrap-around)
        const nextSet = [];
        for (let i = 0; i < IMAGES_PER_SET; i++) {
          const imageIndex = (nextIndex + i) % allImages.length;
          nextSet.push(allImages[imageIndex]);
        }
        
        setCurrentImageSet(nextSet);
        return nextIndex;
      });
    }, ROTATION_INTERVAL);

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, [allImages]);

  // Optional: Manual navigation functions
  const goToNextSet = () => {
    if (allImages.length <= IMAGES_PER_SET) return;
    
    setCurrentIndex((prevIndex) => {
      const nextIndex = (prevIndex + IMAGES_PER_SET) % allImages.length;
      const nextSet = [];
      
      for (let i = 0; i < IMAGES_PER_SET; i++) {
        const imageIndex = (nextIndex + i) % allImages.length;
        nextSet.push(allImages[imageIndex]);
      }
      
      setCurrentImageSet(nextSet);
      return nextIndex;
    });
  };

  const goToPrevSet = () => {
    if (allImages.length <= IMAGES_PER_SET) return;
    
    setCurrentIndex((prevIndex) => {
      const nextIndex = (prevIndex - IMAGES_PER_SET + allImages.length) % allImages.length;
      const nextSet = [];
      
      for (let i = 0; i < IMAGES_PER_SET; i++) {
        const imageIndex = (nextIndex + i) % allImages.length;
        nextSet.push(allImages[imageIndex]);
      }
      
      setCurrentImageSet(nextSet);
      return nextIndex;
    });
  };

  return (
    <Box
      className="galleryBackground"
      sx={{
        width: "100%",
        height: "auto",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: { xs: "20px", sm: "40px" },
      }}
    >
        <Box sx={{ width: "100%", maxWidth: "1200px", display: 'flex', position: 'relative', 
            flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
            <Box className="gallery-box" sx={{ textAlign: "center", mb: 4 }}>
                <Box className='gallery-after'>
                    <Typography variant="h4" gutterBottom>
                    Our Gallery
                    </Typography>
                    <PrimaryButton tittle="View More" bcolor="#ffffffff" tcolor="#90ca25" link={'/gallery'} />
                </Box>
            </Box>
            <Box sx={{ flex: 1, minWidth: {xs: '300px', sm: '300px'}, maxWidth: '900px' }}>
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        alignItems: 'center',
                        justifyContent: {xs: 'center', sm: 'center', md: 'center'},
                        position: 'relative',
                    }}>
                    {currentImageSet.length > 0 ? (
                        currentImageSet.map((gall, index) => (
                        <Box
                            key={`${gall.id}-${currentIndex}-${index}`}
                            sx={{
                            flex: 1,
                            minWidth: { xs: "250px", sm: "80px", md: '100px' },
                            maxWidth: { xs: "300px", sm: "150px", md: '200px' },
                            height: { xs: "300px", sm: "100px", md: '200px' },
                            borderRadius: "10px",
                            overflow: "hidden",
                            transition: "opacity 0.5s ease-in-out",
                            }}
                            // Add AOS attributes if using AOS
                            data-aos="zoom-in"
                            data-aos-delay={200 + index * 100}
                        >
                            <img
                            src={`https://mindsinaction.com.na/assets/media/pictures/${gall.image}`}
                            alt={gall.title || "gallery"}
                            style={{ 
                                width: "100%", 
                                height: "100%", 
                                objectFit: "cover",
                                transition: "transform 0.5s ease"
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.05)"}
                            onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                            />
                        </Box>
                        ))
                    ) : (
                        <Typography>No images available</Typography>
                    )}
                    
                    {/* Optional: Navigation indicators */}
                    {allImages.length > IMAGES_PER_SET && (
                      <Box sx={{
                        position: 'absolute',
                        bottom: '-30px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: '8px'
                      }}>
                        {Array.from({ length: Math.ceil(allImages.length / IMAGES_PER_SET) }).map((_, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              width: '10px',
                              height: '10px',
                              borderRadius: '50%',
                              bgcolor: Math.floor(currentIndex / IMAGES_PER_SET) === idx ? '#90ca25' : '#ccc',
                              cursor: 'pointer',
                              transition: 'background-color 0.3s'
                            }}
                            onClick={() => {
                              const newIndex = idx * IMAGES_PER_SET;
                              const newSet = allImages.slice(newIndex, newIndex + IMAGES_PER_SET);
                              setCurrentIndex(newIndex);
                              setCurrentImageSet(newSet);
                            }}
                          />
                        ))}
                      </Box>
                    )}
                </Box>
                <Typography variant="h6" sx={{ mt: 4, textAlign: 'justify' }}>
                    With the hands-on approach to STEAM, we cultivate a generation of innovators by providing industry 4.0 revolution skills to unleash the true potential of young minds
                </Typography>
            </Box>
        </Box>

        <Box sx={{ width: "100%", maxWidth: "1200px" }}>
            {/* Video Gallery (YouTube iframes) */}
            {gallVideo.length > 0 && (
            <Box
                sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                justifyContent: "center",
                mt: 4,
                }}
            >
                {gallVideo.map((vid) => {
                const videoId = getYouTubeVideoId(vid.video);
                return videoId ? (
                    <Box
                    key={vid.id}
                    sx={{
                        width: { xs: "300px", sm: "270px" },
                        height: { xs: "300px", sm: "200px" },
                        borderRadius: "10px",
                        overflow: "hidden",
                    }}
                    data-aos="zoom-in"
                    data-aos-delay={100}
                    >
                    <iframe
                        width="100%"
                        height="100%"
                        src={`https://www.youtube.com/embed/${videoId}`}
                        title={vid.title || "YouTube video"}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                    </Box>
                ) : null;
                })}
            </Box>
            )}
        </Box>
    </Box>
  );
};

export default Gallery;